/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { Validators } from '@angular/forms';
import { type Command, type Rest, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type UnixLocalGroup } from 'app/common/types/mo';
import { SWITCH } from 'app/config/switch';

import { type UnixGroupVm } from '../../../access-user';
import { AccessUserValidators } from '../../../access-user.validator';
import { AccessUserStrService } from '../../../access-user-str.service';
import { UnixLocalAccessGroupService } from '../unix-local-access-group.service';

@Component({
    selector: 'app-create-unix-local-access-group',
    templateUrl: './create-unix-local-access-group.component.html',
    styleUrls: ['./create-unix-local-access-group.component.scss'],
})
export class CreateUnixLocalAccessGroupComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() vstore: SelectOption;
    i18n = this.str.i18n;
    closeArgs = { refresh: false };
    windowVisible = true;
    SWITCH = SWITCH;

    errorMessage: any;
    vm: FormGroup;

    get vmValue(): UnixGroupVm {
        return this.vm ? this.vm.value : null;
    }

    /**
     * ID的范围， 0 - 4294967295
     */
    idRange = {
        min: 0,
        max: 4294967295,
    };

    // 多租户对象下拉列表选项
    vstoreRest: Rest;
    selectedVstore: SelectOption;

    constructor(
        private localGroupService: UnixLocalAccessGroupService,
        private str: AccessUserStrService,
        injector: Injector,
        private toolService: ToolService,
    ) {
        super(injector);
        this.vstoreRest = this.localGroupService.getVstoreRest();
    }

    ngOnInit(): void {
        super.ngOnInit();
        // 租户视图进入不再支持选租户，文本展示
        if (this.vstore && this.vstore.value) {
            this.selectedVstore = this.vstore;
            this.vm.get('vstoreItem').disable();
        } else {
            this.vm.get('vstoreItem').enable();
        }
    }

    initVm(): void {
        this.vm = new FormGroup({
            vstoreItem: new FormControl(null, {
                validators: [
                    Validators.required,
                ],
            }),
            name: new FormControl('', [
                Validators.required,
                DMValidators.rangeByteLength(1, 64),
                AccessUserValidators.accessName,
            ]),
            id: new FormControl(),
            description: new FormControl('', [
                DMValidators.rangeByteLength(0, 255),
            ]),
        }, {
            updateOn: 'blur',
        });

        this.errorMessage = {
            vstoreItem: {
                required: this.toolService.requiredErr(this.i18n.vStoreLab),
            },
            name: {
                required: this.i18n.error.getRequired(this.i18n.name),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 64),
                accessName: this.i18n.error.accessName,
            },
            description: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
        };
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [this.buildCreateLocalGroupCmd()];
        return cmds;
    }

    buildCreateLocalGroupCmd(): Command {
        const vmValue = this.vmValue;
        const params: UnixLocalGroup = {
            name: vmValue.name,
            description: vmValue.description,
        };
        // 排除!0为true
        if (vmValue.id || vmValue.id === 0) {
            params.id = vmValue.id;
        }
        if (this.selectedVstore) {
            params.vstoreId = this.selectedVstore.value;
        }
        return {
            name: this.i18n.rest.unixGroup.create,
            nameParams: [params.name],
            action: this.localGroupService.create(params),
        };
    }
}
